import { BuildInfo } from "./build-info";
import { ConfigurationDelta } from "src/core";

export class ConfigurationHistoryManager {
	private _past: (ConfigurationDelta | undefined)[] = [];
	private _present: ConfigurationDelta | undefined;
	private _future: (ConfigurationDelta | undefined)[] = [];
	private _wasHistoryAltered = false;
	private _maxItems = 20;

	constructor() {}

	// Add a new item to the history
	addToHistory(configuration: ConfigurationDelta): void {
		/* Since using undo or redo on history will trigger a configurationChange event,
		 * which in turn is programmed to trigger the save event for our history. We always ignore the
		 * first save after history has been altered using (undo/redo) to avoid an infinite loop. */
		if (this._wasHistoryAltered) {
			this._wasHistoryAltered = false;
			return;
		}

		/* Recording the configuration that is already present changes nothing, so it
		 * must not create an entry. Callers seed the baseline independently of each
		 * other — a stored-session boot seeds once when the restore completes and
		 * again when the constructor finishes — and an unguarded re-seed turns the
		 * baseline itself into an undo entry that reverts to the state the user is
		 * already in. Idempotency belongs here rather than in any one caller: no
		 * caller can create a phantom entry, and no two adjacent entries are equal,
		 * so undo always produces an actual change. */
		if (this._isPresentConfiguration(configuration)) {
			return;
		}

		if (this._present) {
			this._past.push(this._present);
		}
		this._present = configuration;
		this._future = [];

		// Ensure the history does not exceed the maximum limit
		if (this._past.length > this._maxItems) {
			this._past.shift();
		}

		// this.log("PUSH");
	}

	/* Configurations are compared by serialization. They are plain
	 * JSON-serializable trees (the same shape that gets stored as session data),
	 * produced by the same serializer over the same state tree, so key order is
	 * stable between calls. A false negative only degrades to the previous
	 * behaviour — one extra entry — and can never drop a real one. */
	private _isPresentConfiguration(configuration: ConfigurationDelta): boolean {
		return (
			this._present !== undefined &&
			JSON.stringify(this._present) === JSON.stringify(configuration)
		);
	}

	clearHistory(): void {
		this._past = [];
		this._present = undefined;
		this._future = [];
	}

	// Undo the last action
	getPrevious(): ConfigurationDelta | undefined {
		if (this.canUndo()) {
			this._future.push(this._present);
			this._present = this._past.pop();
			this._wasHistoryAltered = true;
			// this.log("UNDO");
		}

		return this._present ? this._present : undefined;
	}

	// Redo the last undone action
	getNext(): ConfigurationDelta | undefined {
		if (this.canRedo()) {
			this._past.push(this._present);
			this._present = this._future.pop();
			this._wasHistoryAltered = true;
			// this.log("REDO");
		}

		return this._present ? this._present : undefined;
	}

	canUndo(): boolean {
		return this._past.length > 0;
	}

	canRedo(): boolean {
		return this._future.length > 0;
	}

	log(action: string): void {
		if (BuildInfo.mode === "development") {
			const past = this._past.map((_config, index) => index);
			const present = this._past.length.toString();
			const future = this._future.map(
				(_config, index) => index + past.length + 1,
			);
			// eslint-disable-next-line no-console
			console.log(`HISTORY:${action}`, ...past, present, ...future);
		}
	}
}
